import { useAssistantRuntime, useAuiState } from '@assistant-ui/react'
import { useQueryClient } from '@tanstack/react-query'
import { MessageSquarePlusIcon, Plus } from 'lucide-react'
import { type FC, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { AgentThread } from '@/components/agent/AgentThread'
import { AgentThreadList, type AgentThreadListProps } from '@/components/agent/AgentThreadList'
import { AgentThreadSwitcher } from '@/components/agent/AgentThreadSwitcher'
import { ThreadContextIndicator } from '@/components/agent/ThreadContextIndicator'
import { useAgentSidebar } from '@/components/layout/AgentSidebarContext'
import { useToolbar } from '@/components/layout/ToolbarContext'
import { Button } from '@/components/ui/button'
import { AgentChatHost } from '@/features/agent/AgentChatHost'
import { agentThreadsKey, useThreadList } from '@/features/agent/useAgentRuntime'
import { useAgentThreadSelection } from '@/features/cases/hooks/useAgentThreadSelection'
import { useCaseContext } from '../../CaseContext'

/**
 * Fires a pending prompt into the assistant runtime once the thread is
 * empty and ready. Lives inside an AgentChatHost so `useAssistantRuntime`
 * resolves. The `useRef` guard prevents double-append under StrictMode and
 * re-renders.
 */
const InitialPromptDispatcher: FC<{ prompt: string; onConsumed: () => void }> = ({
  prompt,
  onConsumed,
}) => {
  const runtime = useAssistantRuntime()
  const isEmpty = useAuiState(s => s.thread.isEmpty)
  const sentRef = useRef(false)
  useEffect(() => {
    if (sentRef.current || !prompt || !isEmpty) return
    sentRef.current = true
    runtime.thread.append({
      role: 'user',
      content: [{ type: 'text', text: prompt }],
    })
    onConsumed()
  }, [prompt, isEmpty, runtime, onConsumed])
  return null
}

export default function AgentPage() {
  const { t } = useTranslation()
  const { caseId } = useCaseContext()
  const { dispatchGraphAction, open, setOpen } = useAgentSidebar()
  const queryClient = useQueryClient()
  // Fetch ALL threads — the list shows chats only, but deep links to run
  // threads still need resolving so they can open in the main panel.
  const threadList = useThreadList(caseId)
  const { threads, create } = threadList

  // Close the agent sidebar when entering this page; restore on leave
  useEffect(() => {
    const wasOpen = open
    setOpen(false)
    queryClient.invalidateQueries({ queryKey: agentThreadsKey(caseId) })
    queryClient.invalidateQueries({ queryKey: ['agent', 'history', caseId] })
    return () => {
      if (wasOpen) setOpen(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId])

  const {
    activeThreadId,
    setActiveThreadId,
    pendingPrompt,
    clearPendingPrompt,
    handleCreate,
    handleDelete,
    handleRename,
  } = useAgentThreadSelection(threadList)

  const threadListProps: AgentThreadListProps = {
    threads,
    activeThreadId,
    onSelect: setActiveThreadId,
    onCreate: handleCreate,
    onDelete: handleDelete,
    onRename: handleRename,
    isCreating: create.isPending,
  }

  useToolbar(
    // biome-ignore lint/correctness/useExhaustiveDependencies: the handlers are new on every render (listing them would re-register the toolbar each time); what they read is listed
    useMemo(
      () => (
        <>
          {/* Phone: the thread-list column is hidden; this opens it as a sheet. */}
          <AgentThreadSwitcher
            title={
              activeThreadId
                ? (threads.find(thread => thread.id === activeThreadId)?.title ??
                  t('agentSidebar.newConversation'))
                : t('agentSidebar.threads')
            }
            label={t('agentSidebar.threads')}
            list={threadListProps}
          />
          <Button
            size="xs"
            className="max-md:h-9"
            onClick={handleCreate}
            disabled={create.isPending}
          >
            <Plus className="size-3" />
            {t('common.newConversation')}
          </Button>
        </>
      ),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [create.isPending, t, threads, activeThreadId]
    )
  )

  return (
    <div className="flex h-full min-h-0 w-full bg-background">
      {/* Chat area */}
      <div className="flex flex-1 min-w-0 flex-col">
        {activeThreadId ? (
          <AgentChatHost
            key={activeThreadId}
            caseId={caseId}
            threadId={activeThreadId}
            currentView="agent"
            onGraphAction={dispatchGraphAction}
            onRequestNewThread={() => void handleCreate()}
          >
            <div className="min-h-0 flex-1">
              {pendingPrompt && (
                <InitialPromptDispatcher prompt={pendingPrompt} onConsumed={clearPendingPrompt} />
              )}
              <AgentThread
                maxWidth="920px"
                background="background"
                composerFooter={
                  <div className="flex items-center justify-end px-1 text-2xs text-muted-foreground">
                    <ThreadContextIndicator />
                  </div>
                }
              />
            </div>
          </AgentChatHost>
        ) : (
          <div className="flex h-full flex-col items-center justify-center gap-3 text-muted-foreground max-md:px-4 max-md:text-center">
            <p className="text-sm">{t('cases.agent.selectOrNew')}</p>
            <Button
              variant="outline"
              size="sm"
              className="max-md:h-10"
              onClick={handleCreate}
              disabled={create.isPending}
            >
              <MessageSquarePlusIcon className="size-4 mr-2" />
              {t('common.newConversation')}
            </Button>
          </div>
        )}
      </div>

      {/* Thread list sidebar — from md; on a phone it lives in the toolbar's sheet */}
      <div className="hidden w-72 shrink-0 md:block">
        <AgentThreadList {...threadListProps} />
      </div>
    </div>
  )
}
